import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate, useLocation } from 'react-router-dom';
import Avatar from '../Avatar';
import styles from '../../styles/Team.module.css';

const MusukoLogo = () => (
  <div className={styles.logoContainer}>
    <div className={styles.logoIcon}>
      <FontAwesomeIcon icon="rocket" />
    </div>
    <div className={styles.logoText}>
      <span className={styles.logoMain}>MUSUKO</span>
      <span className={styles.logoSub}>analytics</span>
    </div>
  </div>
);

const DashboardHeader = ({ user, onLogout, onPageSelectorOpen, onDateFilterOpen, selectedPage, pages, timeRange, showPageSelector = true }) => {
  const [showMobileNav, setShowMobileNav] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const location = useLocation();
  
  // Hide page selector on affiliate and monetized pages
  const shouldShowPageSelector = !location.pathname.includes('/affiliate') && !location.pathname.includes('/monetized');
  
  const selectedPageName = selectedPage === 'overall' 
    ? 'All Pages' 
    : pages.find(p => p.page_id === selectedPage)?.page_name || 'Select Page';

  const handleUserMenuClick = () => {
    const isMobile = window.innerWidth <= 768;
    
    if (isMobile) {
      setShowMobileNav(!showMobileNav);
      setShowUserDropdown(false);
    } else {
      setShowUserDropdown(!showUserDropdown);
      setShowMobileNav(false);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const userMenuContainer = event.target.closest(`.${styles.userMenuContainer}`);
      if (!userMenuContainer) {
        setShowUserDropdown(false);
        setShowMobileNav(false);
      }
    };

    if (showUserDropdown || showMobileNav) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserDropdown, showMobileNav]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth <= 768;
      if (isMobile && showUserDropdown) {
        setShowUserDropdown(false);
      } else if (!isMobile && showMobileNav) {
        setShowMobileNav(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [showUserDropdown, showMobileNav]);

  return (
    <header className={styles.dashboardHeader}>
      <div className={styles.headerLeft}>
        <MusukoLogo />
      </div>

      <div className={styles.headerRight}>
        {/* Page Selector Button - Only show on Dashboard pages */}
        {shouldShowPageSelector && (
          <button className={styles.pageSelectorBtn} onClick={onPageSelectorOpen}>
            <FontAwesomeIcon icon="globe" />
            <span>{selectedPageName}</span>
            <FontAwesomeIcon icon="chevron-down" />
          </button>
        )}

        {/* Date Filter Button */}
        <button className={styles.headerBtn} onClick={onDateFilterOpen}>
          <FontAwesomeIcon icon="calendar" />
        </button>

        {/* User Menu */}
        <div className={styles.userMenuContainer}>
          <button 
            className={styles.userMenuBtn}
            onClick={handleUserMenuClick}
          >
            <Avatar user={user} size={32} />
            <span className={styles.userName}>{user?.name || 'User'}</span>
          </button>
          
          {/* Desktop User Dropdown */}
          {showUserDropdown && (
            <div className={styles.userDropdown}>
              <div className={styles.userInfo}>
                <Avatar user={user} size={48} />
                <div className={styles.userDetails}>
                  <div className={styles.userFullName}>{user?.name || 'User'}</div>
                  <div className={styles.userEmail}>{user?.email || 'user@example.com'}</div>
                </div>
              </div>
              <div className={styles.userDropdownDivider}></div>
              <button className={styles.logoutBtn} onClick={onLogout}>
                <FontAwesomeIcon icon="sign-out-alt" />
                Sign Out
              </button>
            </div>
          )}
          
          {/* Mobile Navigation Modal */}
          <MobileNavigationModal
            isOpen={showMobileNav}
            onClose={() => setShowMobileNav(false)}
            user={user}
            onLogout={onLogout}
            onPageSelectorOpen={onPageSelectorOpen}
            onDateFilterOpen={onDateFilterOpen}
            selectedPage={selectedPage}
            pages={pages}
            timeRange={timeRange}
            showPageSelector={showPageSelector}
          />
        </div>
      </div>
    </header>
  );
};

const MobileNavigationModal = ({ isOpen, onClose, user, onLogout, onPageSelectorOpen, onDateFilterOpen, selectedPage, pages, timeRange, showPageSelector = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const selectedPageName = selectedPage === 'overall' 
    ? 'All Pages' 
    : pages.find(p => p.page_id === selectedPage)?.page_name || 'Select Page';
    
  // Hide page selector on affiliate and monetized pages
  const shouldShowPageSelector = !location.pathname.includes('/affiliate') && !location.pathname.includes('/monetized');

  const navItems = [
    { path: '/team', label: 'Dashboard', icon: <FontAwesomeIcon icon="chart-line" /> },
    { path: '/team/affiliate', label: 'Affiliate', icon: <FontAwesomeIcon icon="handshake" /> },
    { path: '/team/monetized', label: 'Monetized', icon: <FontAwesomeIcon icon="dollar-sign" /> }
  ];

  const handleNavigation = (path) => {
    navigate(path);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.mobileModalOverlay} onClick={onClose}>
      <div className={styles.mobileNavModal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.modalUserInfo}>
            <Avatar user={user} size={48} />
            <div className={styles.modalUserDetails}>
              <div className={styles.modalUserName}>{user?.name || 'User'}</div>
              <div className={styles.modalUserEmail}>{user?.email || 'user@example.com'}</div>
            </div>
          </div>
          <button className={styles.modalCloseBtn} onClick={onClose}>
            <FontAwesomeIcon icon="times" />
          </button>
        </div>

        <div className={styles.modalContent}>
          {/* Navigation Section */}
          <div className={styles.navigationSection}>
            <h3 className={styles.sectionTitle}>
              <FontAwesomeIcon icon="map" />
              Navigation
            </h3>
            <div className={styles.navigationItems}>
              {navItems.map(item => {
                const isActive = item.path === '/team' 
                  ? location.pathname === '/team'
                  : location.pathname.startsWith(item.path);
                
                return (
                  <button 
                    key={item.path}
                    className={`${styles.navigationItem} ${isActive ? styles.navigationItemActive : ''}`}
                    onClick={() => handleNavigation(item.path)}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Quick Actions Section */}
          <div className={styles.quickActionsSection}>
            <h3 className={styles.sectionTitle}>
              <FontAwesomeIcon icon="cog" />
              Quick Actions
            </h3>
            <div className={styles.quickActionsGrid}>
              {/* Only show page selector on Dashboard pages */}
              {shouldShowPageSelector && (
                <button className={styles.quickActionBtn} onClick={() => { onPageSelectorOpen(); onClose(); }}>
                  <FontAwesomeIcon icon="globe" />
                  <span>Change Page</span>
                  <div className={styles.quickActionValue}>{selectedPageName}</div>
                </button>
              )}
              <button className={styles.quickActionBtn} onClick={() => { onDateFilterOpen(); onClose(); }}>
                <FontAwesomeIcon icon="calendar" />
                <span>Date Filter</span>
                <div className={styles.quickActionValue}>{timeRange} days</div>
              </button>
            </div>
          </div>

          {/* User Actions Section */}
          <div className={styles.userActionsSection}>
            <h3 className={styles.sectionTitle}>
              <FontAwesomeIcon icon="user" />
              Account
            </h3>
            <div className={styles.userActions}>
              <button className={styles.userActionBtn} onClick={onLogout}>
                <FontAwesomeIcon icon="sign-out-alt" />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardHeader; 